import { fn } from '@ember/helper';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import EmberPopover from 'ember-tooltips/components/ember-popover';
import LineClamp from 'garaje/components/line-clamp';
import dateFormat from 'garaje/helpers/date-format';
import removedString from 'garaje/helpers/removed-string';
import type Entry from 'garaje/models/entry';
import type Invite from 'garaje/models/invite';
import italicizeIfRemoved from 'garaje/modifiers/italicize-if-removed';
import type CurrentLocationService from 'garaje/services/current-location';
import { isSensitiveFieldMasked, type SensitiveState } from 'garaje/services/sensitive-fields-visibility';
import type SensitiveFieldsVisibilityService from 'garaje/services/sensitive-fields-visibility';

interface CustomColumnSignature {
  Args: {
    name: string;
    content: Invite | Entry;
  };
  Element: HTMLDivElement;
}

export default class CustomColumn extends Component<CustomColumnSignature> {
  @service declare currentLocation: CurrentLocationService;
  @service declare sensitiveFieldsVisibility: SensitiveFieldsVisibilityService;
  @tracked nameWasTruncated = false;

  get sensitiveState(): SensitiveState | undefined {
    return this.args.content.sensitiveFields?.[this.args.name];
  }

  get isSensitiveMasked(): boolean {
    return isSensitiveFieldMasked(this.sensitiveState, this.sensitiveFieldsVisibility.isRevealed(this.args.name));
  }

  get data(): string {
    if (this.isSensitiveMasked) {
      return 'Hidden';
    }

    const userData = this.args.content.userData;

    if (userData) {
      const matchingField = userData.findBy('field', this.args.name);
      if (matchingField) {
        const val = matchingField.value;

        if (/^\d{4}-\d{2}-\d{2}$/.test(val)) {
          return dateFormat(val, 'MMM d, yyyy', { parseFormat: 'yyyy-MM-dd' });
        }

        return val;
      }
    }
    return '';
  }

  <template>
    <div data-test-custom-field class="table-cell border-b align-middle p-2 text-carbon-50" ...attributes>
      <div class="h-12 flex items-center">
        <span data-test-custom-column={{@name}} class={{if this.isSensitiveMasked "italic"}}>
          <LineClamp
            @useNative={{true}}
            @handleTruncate={{fn (mut this.nameWasTruncated)}}
            @lines={{2}}
            @lineHeight={{1.25}}
            @text={{removedString this.data}}
            {{italicizeIfRemoved this.data}}
          />
        </span>

        {{#if this.nameWasTruncated}}
          <EmberPopover
            @tooltipClass="break-word max-w-sm"
            @popperContainer=".ui__page"
            @delay={{400}}
            @side="top"
            @text={{removedString this.data}}
          />
        {{/if}}
      </div>
    </div>
  </template>
}
